import React, { useMemo, useCallback, useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { EntityTablePage, SaveConfirmationConfig, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getTimecardTableConfig, MarketOption } from "@/config/tableConfigs/timecardTableConfig";
import { supabase } from "@/lib/supabase-client";
import {
  timecardService,
  extractBillableSegments,
  extractPayableSegments,
  getSegmentDuration,
  getModifierMultiplier,
} from "@/services/timecardService";
import { billingItemService } from "@/services/billingItemService";
import { payrollItemService } from "@/services/payrollItemService";
import { TimecardType } from "@/types/db/time_cards";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { FromDB } from "@/types";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { ApproveTimecardModal, CopyOption } from "@/components/Modals/ApproveTimecardModal";
import { DeleteTimecardModal } from "@/components/Modals/DeleteTimecardModal";
import { DuplicateItemsWarningModal } from "@/components/Modals/DuplicateItemsWarningModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useContextMenu from "@/hooks/useContextMenu";
import { useTheme } from "@/hooks/useTheme";
import { useModal } from "@/hooks/useModal";
import { Button } from "@/components/ui/Button";
import { router } from "expo-router";
import * as Clipboard from "expo-clipboard";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function TimecardsPage() {
  const { userRoles, displayNames } = useAuth();
  const toast = useToast();
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const { triggerRefresh } = useRowSelectionContext();
  const modal = useModal();
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  useSetPageTitle(displayNames?.time_cards?.plural);

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const tableConfig = useMemo(
    () => getTimecardTableConfig(userRoles, marketOptions, displayNames),
    [userRoles, marketOptions, displayNames],
  );

  // Helper to show duplicate warning modal and wait for user response
  const showDuplicateWarning = useCallback(
    (type: "billing" | "payroll" | "both", billingCount: number, payrollCount: number): Promise<boolean> => {
      return new Promise((resolve) => {
        modal.openModal(
          <DuplicateItemsWarningModal
            type={type}
            billingCount={billingCount}
            payrollCount={payrollCount}
            onConfirm={() => {
              modal.closeModal();
              resolve(true);
            }}
            onCancel={() => {
              modal.closeModal();
              resolve(false);
            }}
          />,
        );
      });
    },
    [modal],
  );

  // Helper to create billing items from a single timecard (segment-based)
  const createBillingItemsForTimecard = useCallback((timecard: any): any[] => {
    const items: any[] = [];
    const clientId = timecard?.client?.id;
    if (!clientId) return items;

    const billingOverride = timecard.meta_data?.billing_rate_override;
    const positionData = timecard.position_data;
    const billingRate = positionData?.meta_data?.billing_rate;
    const time = typeof timecard.time === "string" ? JSON.parse(timecard.time) : timecard.time;

    // Common metadata for all items (IDs only - names are enriched on fetch)
    const commonMeta = {
      worker: {
        id: timecard.user?.id,
        first_name: timecard.user?.first_name,
        last_name: timecard.user?.last_name,
      },
      shift_id: timecard.shift_id,
      project_id: timecard.project_id,
      position_name: positionData?.name || null,
    };

    // FLAT RATE: Single item with flat amount
    if (billingOverride?.rate === "flat") {
      const flatAmount = billingOverride.amount || 0;
      items.push({
        client_id: clientId,
        time_card_id: timecard.id,
        amount: flatAmount,
        description: `Flat rate: $${flatAmount.toFixed(2)}`,
        rate: `$${flatAmount.toFixed(2)}`,
        quantity: 1,
        unit: "-",
        start_time: time?.start_time || null,
        end_time: time?.end_time || null,
        ...commonMeta,
      });
      return items;
    }

    // DAILY RATE: Single item if any billable time exists
    if (billingRate?.rate === "daily") {
      const segments = extractBillableSegments(timecard);
      if (segments.length > 0) {
        items.push({
          client_id: clientId,
          time_card_id: timecard.id,
          amount: billingRate.amount,
          description: `$${billingRate.amount}/day`,
          rate: `$${billingRate.amount}/day`,
          quantity: 1,
          unit: "days",
          start_time: time?.start_time || null,
          end_time: time?.end_time || null,
          ...commonMeta,
        });
      }
      return items;
    }

    // HOURLY RATE: Per-segment items
    const segments = extractBillableSegments(timecard);
    let cursor = time?.start_time ? new Date(time.start_time).getTime() : null;
    for (const segment of segments) {
      const duration = Math.round(getSegmentDuration(segment) * 100) / 100;
      if (duration <= 0) continue;

      const segStart = segment.start_time || (cursor ? new Date(cursor).toISOString() : null);
      const segEnd = segment.end_time || (cursor ? new Date(cursor + duration * 3600000).toISOString() : null);
      if (cursor) cursor += duration * 3600000;

      const baseRate = billingRate?.amount || 0;
      const modifierMultiplier = getModifierMultiplier(segment.modifiers);
      const effectiveRate = baseRate * modifierMultiplier;
      const amount = duration * effectiveRate;

      // Build description and rate string
      let description = `${duration.toFixed(2)} hrs @ $${baseRate.toFixed(2)}/hr`;
      let rateStr = `$${effectiveRate.toFixed(2)}/hr`;
      if (modifierMultiplier > 1) {
        const modifierStr = segment.modifiers?.join(", ") || "";
        description += ` (${modifierStr} ${modifierMultiplier}x)`;
        rateStr = `$${effectiveRate.toFixed(2)}/hr (${modifierStr})`;
      }

      items.push({
        client_id: clientId,
        time_card_id: timecard.id,
        amount: Math.round(amount * 100) / 100,
        description,
        rate: rateStr,
        quantity: duration,
        unit: segment.type || "worked",
        start_time: segStart,
        end_time: segEnd,
        segment_type: segment.type || "worked",
        modifiers: segment.modifiers || [],
        ...commonMeta,
      });
    }

    return items;
  }, []);

  // Helper to create payroll items from a single timecard (segment-based)
  const createPayrollItemsForTimecard = useCallback((timecard: any): any[] => {
    const items: any[] = [];
    const clientId = timecard?.client?.id;
    if (!clientId) return items;

    const payrollOverride = timecard.meta_data?.payroll_rate_override;
    const positionData = timecard.position_data;
    const payrollRateMeta = positionData?.meta_data?.payroll_rate;
    const time = typeof timecard.time === "string" ? JSON.parse(timecard.time) : timecard.time;

    // Common metadata for all items (IDs only - names are enriched on fetch)
    const commonMeta = {
      worker: {
        id: timecard.user?.id,
        first_name: timecard.user?.first_name,
        last_name: timecard.user?.last_name,
      },
      shift_id: timecard.shift_id,
      project_id: timecard.project_id,
      position_name: positionData?.name || null,
    };

    // Extract user's pay rate for base_rate/base_add calculations
    // rawUserRate can be a number (base rate) or object {rate, amount} (per-position override)
    const rawUserRate = timecard.user_pay_snapshot?.meta_data?.rate;
    const hasPayOverride = typeof rawUserRate === "object" && rawUserRate !== null;
    const payOverrideAmount = hasPayOverride ? rawUserRate?.amount || 0 : 0;
    const baseUserRate = hasPayOverride ? 0 : typeof rawUserRate === "number" ? rawUserRate : 0;

    // Calculate effective payroll rate (needed before flat rate check for reverse engineering hours)
    let rateAmount = 0;
    let rateDescription = "";
    let unit = "hours";

    if (payrollRateMeta) {
      if (payrollRateMeta.rate === "base_rate") {
        rateAmount = baseUserRate * (payrollRateMeta.multiplier || 1);
        rateDescription = `$${rateAmount.toFixed(2)}/hr (${payrollRateMeta.multiplier}x base)`;
      } else if (payrollRateMeta.rate === "base_add") {
        rateAmount = baseUserRate + (payrollRateMeta.offset || 0);
        rateDescription = `$${rateAmount.toFixed(2)}/hr (+$${payrollRateMeta.offset})`;
      } else if (payrollRateMeta.rate === "daily") {
        rateAmount = payrollRateMeta.amount || 0;
        unit = "days";
        rateDescription = `$${rateAmount.toFixed(2)}/day`;
      } else {
        // hourly
        rateAmount = payrollRateMeta.amount || 0;
        rateDescription = `$${rateAmount.toFixed(2)}/hr`;
      }
    }

    // Per-position override: use if higher than position formula result
    if (payOverrideAmount > 0 && rateAmount > 0) {
      if (payOverrideAmount > rateAmount) {
        rateAmount = payOverrideAmount;
        unit = "hours";
        rateDescription = `$${rateAmount.toFixed(2)}/hr`;
      }
    } else if (payOverrideAmount > 0 && rateAmount === 0) {
      rateAmount = payOverrideAmount;
      unit = "hours";
      rateDescription = `$${rateAmount.toFixed(2)}/hr`;
    }

    // Fallback: no payroll formula and no override — use user's base rate
    if (rateAmount === 0 && baseUserRate > 0) {
      rateAmount = baseUserRate;
      rateDescription = `$${rateAmount.toFixed(2)}/hr`;
    }

    // FLAT RATE: Reverse engineer hours and create segment-based items
    if (payrollOverride?.rate === "flat") {
      const flatAmount = payrollOverride.amount || 0;

      // If we have an hourly rate, create segment-based items using actual hours
      if (rateAmount > 0) {
        const segments = extractPayableSegments(timecard);
        let cursor = time?.start_time ? new Date(time.start_time).getTime() : null;

        for (const segment of segments) {
          const duration = Math.round(getSegmentDuration(segment) * 100) / 100;
          if (duration <= 0) continue;

          const segStart = segment.start_time || (cursor ? new Date(cursor).toISOString() : null);
          const segEnd = segment.end_time || (cursor ? new Date(cursor + duration * 3600000).toISOString() : null);
          if (cursor) cursor += duration * 3600000;

          const modifierMultiplier = getModifierMultiplier(segment.modifiers);
          const effectiveRate = rateAmount * modifierMultiplier;
          const amount = duration * effectiveRate;

          // Build description and rate string
          let description = `${duration.toFixed(2)} hrs @ $${rateAmount.toFixed(2)}/hr`;
          let rateStr = `$${effectiveRate.toFixed(2)}/hr`;
          if (modifierMultiplier > 1) {
            const modifierStr = segment.modifiers?.join(", ") || "";
            description += ` (${modifierStr} ${modifierMultiplier}x)`;
            rateStr = `$${effectiveRate.toFixed(2)}/hr (${modifierStr})`;
          }

          items.push({
            client_id: clientId,
            time_card_id: timecard.id,
            amount: Math.round(amount * 100) / 100,
            description,
            rate: rateStr,
            quantity: duration,
            unit: segment.type || "worked",
            start_time: segStart,
            end_time: segEnd,
            segment_type: segment.type || "worked",
            modifiers: segment.modifiers || [],
            ...commonMeta,
          });
        }
        return items;
      }

      // Fallback: no hourly rate available, use original flat behavior
      items.push({
        client_id: clientId,
        time_card_id: timecard.id,
        amount: flatAmount,
        description: `Flat rate: $${flatAmount.toFixed(2)}`,
        rate: `$${flatAmount.toFixed(2)}`,
        quantity: 1,
        unit: "-",
        start_time: time?.start_time || null,
        end_time: time?.end_time || null,
        ...commonMeta,
      });
      return items;
    }

    // DAILY RATE: Single item if any payable time exists
    if (unit === "days") {
      const segments = extractPayableSegments(timecard);
      if (segments.length > 0) {
        items.push({
          client_id: clientId,
          time_card_id: timecard.id,
          amount: rateAmount,
          description: rateDescription,
          rate: rateDescription,
          quantity: 1,
          unit: "days",
          start_time: time?.start_time || null,
          end_time: time?.end_time || null,
          ...commonMeta,
        });
      }
      return items;
    }

    // HOURLY RATE: Per-segment items
    const segments = extractPayableSegments(timecard);
    let cursor = time?.start_time ? new Date(time.start_time).getTime() : null;
    for (const segment of segments) {
      const duration = Math.round(getSegmentDuration(segment) * 100) / 100;
      if (duration <= 0) continue;

      const segStart = segment.start_time || (cursor ? new Date(cursor).toISOString() : null);
      const segEnd = segment.end_time || (cursor ? new Date(cursor + duration * 3600000).toISOString() : null);
      if (cursor) cursor += duration * 3600000;

      const modifierMultiplier = getModifierMultiplier(segment.modifiers);
      const effectiveRate = rateAmount * modifierMultiplier;
      const amount = duration * effectiveRate;

      // Build description and rate string
      let description = `${duration.toFixed(2)} hrs @ $${rateAmount.toFixed(2)}/hr`;
      let rateStr = `$${effectiveRate.toFixed(2)}/hr`;
      if (modifierMultiplier > 1) {
        const modifierStr = segment.modifiers?.join(", ") || "";
        description += ` (${modifierStr} ${modifierMultiplier}x)`;
        rateStr = `$${effectiveRate.toFixed(2)}/hr (${modifierStr})`;
      }

      items.push({
        client_id: clientId,
        time_card_id: timecard.id,
        amount: Math.round(amount * 100) / 100,
        description,
        rate: rateStr,
        quantity: duration,
        unit: segment.type || "worked",
        start_time: segStart,
        end_time: segEnd,
        segment_type: segment.type || "worked",
        modifiers: segment.modifiers || [],
        ...commonMeta,
      });
    }

    return items;
  }, []);

  // Row action menu content
  const menuContent = (row: any) => {
    return (
      <View>
        <Button
          text="Open"
          iconSize={18}
          icon="open-in-new"
          style={styles.menuItems}
          onPress={() => {
            contextMenu.setVisible(false);
            router.setParams({ panel: "time_card", id: row.id });
          }}
        />
        <Button
          text="Copy as JSON"
          iconSize={18}
          icon="code-json"
          style={styles.menuItems}
          onPress={async () => {
            contextMenu.setVisible(false);
            await Clipboard.setStringAsync(JSON.stringify(row, null, 2));
            toast.showToast({
              title: "Copied to clipboard",
              toastType: "success",
            });
          }}
        />
      </View>
    );
  };

  const headerActions = useMemo(
    () => [
      {
        text: "Copy to Billing",
        icon: "cash-register" as const,
        onPress: async ({ selectedIds, selectedMap }: any) => {
          try {
            // Check for existing billing items (duplicate warning)
            const timecardsWithExistingItems = selectedIds.filter(
              (id: string) => (selectedMap[id]?.billing_items?.length || 0) > 0,
            );

            if (timecardsWithExistingItems.length > 0) {
              const proceed = await showDuplicateWarning("billing", timecardsWithExistingItems.length, 0);
              if (!proceed) return;
            }

            // Create billing items from selected timecards (segment-based)
            const allBillingItems: any[] = [];
            selectedIds.forEach((id: string) => {
              const timecard = selectedMap[id];
              const items = createBillingItemsForTimecard(timecard);
              allBillingItems.push(...items);
            });

            // Filter out items without client_id
            const validBillingItems = allBillingItems.filter((item: any) => item.client_id);

            if (validBillingItems.length === 0) {
              toast.showToast({
                title: "No valid timecards",
                message: "Selected timecards are missing client information or billable segments",
                toastType: "error",
              });
              return;
            }

            const result = await billingItemService.createBillingItems(validBillingItems);
            toast.showToast({
              title: `Created ${result.length} billing item${result.length === 1 ? "" : "s"}`,
              toastType: "success",
            });
            // Trigger refresh for billing_item pages
            triggerRefresh("billing item");
          } catch (error: any) {
            toast.showToast({
              title: "Failed to copy to billing",
              message: error.message,
              toastType: "error",
            });
          }
        },
      },
      {
        text: "Copy to Payroll",
        icon: "account-cash" as const,
        onPress: async ({ selectedIds, selectedMap }: any) => {
          try {
            // Check for existing payroll items (duplicate warning)
            const timecardsWithExistingItems = selectedIds.filter(
              (id: string) => (selectedMap[id]?.payroll_items?.length || 0) > 0,
            );

            if (timecardsWithExistingItems.length > 0) {
              const proceed = await showDuplicateWarning("payroll", 0, timecardsWithExistingItems.length);
              if (!proceed) return;
            }

            // Create payroll items from selected timecards (segment-based)
            const allPayrollItems: any[] = [];
            selectedIds.forEach((id: string) => {
              const timecard = selectedMap[id];
              const items = createPayrollItemsForTimecard(timecard);
              allPayrollItems.push(...items);
            });

            // Filter out items without client_id
            const validPayrollItems = allPayrollItems.filter((item: any) => item.client_id);

            if (validPayrollItems.length === 0) {
              toast.showToast({
                title: "No valid timecards",
                message: "Selected timecards are missing client information or payable segments",
                toastType: "error",
              });
              return;
            }

            const result = await payrollItemService.createPayrollItems(validPayrollItems);
            toast.showToast({
              title: `Created ${result.length} payroll item${result.length === 1 ? "" : "s"}`,
              toastType: "success",
            });
            // Trigger refresh for payroll_item pages
            triggerRefresh("payroll item");
          } catch (error: any) {
            toast.showToast({
              title: "Failed to copy to payroll",
              message: error.message,
              toastType: "error",
            });
          }
        },
      },
      {
        text: "Copy to Both",
        icon: "content-duplicate" as const,
        onPress: async ({ selectedIds, selectedMap }: any) => {
          try {
            // Check for existing billing/payroll items (duplicate warning)
            const timecardsWithBillingItems = selectedIds.filter(
              (id: string) => (selectedMap[id]?.billing_items?.length || 0) > 0,
            );
            const timecardsWithPayrollItems = selectedIds.filter(
              (id: string) => (selectedMap[id]?.payroll_items?.length || 0) > 0,
            );

            if (timecardsWithBillingItems.length > 0 || timecardsWithPayrollItems.length > 0) {
              const proceed = await showDuplicateWarning(
                "both",
                timecardsWithBillingItems.length,
                timecardsWithPayrollItems.length,
              );
              if (!proceed) return;
            }

            // Create billing and payroll items from selected timecards (segment-based)
            const allBillingItems: any[] = [];
            const allPayrollItems: any[] = [];

            selectedIds.forEach((id: string) => {
              const timecard = selectedMap[id];
              const billingItems = createBillingItemsForTimecard(timecard);
              const payrollItems = createPayrollItemsForTimecard(timecard);
              allBillingItems.push(...billingItems);
              allPayrollItems.push(...payrollItems);
            });

            // Filter out items without client_id
            const validBillingItems = allBillingItems.filter((item: any) => item.client_id);
            const validPayrollItems = allPayrollItems.filter((item: any) => item.client_id);

            if (validBillingItems.length === 0 && validPayrollItems.length === 0) {
              toast.showToast({
                title: "No valid timecards",
                message: "Selected timecards are missing client information or billable/payable segments",
                toastType: "error",
              });
              return;
            }

            // Create both billing and payroll items
            const results = await Promise.all([
              validBillingItems.length > 0 ? billingItemService.createBillingItems(validBillingItems) : [],
              validPayrollItems.length > 0 ? payrollItemService.createPayrollItems(validPayrollItems) : [],
            ]);

            const [billingResult, payrollResult] = results;

            toast.showToast({
              title: `Created ${billingResult.length} billing and ${payrollResult.length} payroll items`,
              toastType: "success",
            });
            // Trigger refresh for both billing_item and payroll_item pages
            triggerRefresh("billing item");
            triggerRefresh("payroll item");
          } catch (error: any) {
            toast.showToast({
              title: "Failed to copy to both",
              message: error.message,
              toastType: "error",
            });
          }
        },
      },
    ],
    [toast, triggerRefresh, showDuplicateWarning, createBillingItemsForTimecard, createPayrollItemsForTimecard],
  );

  // Helper to create billing items from patches (segment-based)
  const createBillingItemsFromPatches = useCallback(
    async (patchIds: string[]) => {
      // We need to get the full timecard data for each patch
      const timecards = await timecardService.getTimecards();
      const timecardMap = new Map(timecards.map((tc) => [tc.id, tc]));

      const allBillingItems: any[] = [];
      patchIds.forEach((id) => {
        const timecard = timecardMap.get(id);
        if (!timecard) return;

        const items = createBillingItemsForTimecard(timecard);
        allBillingItems.push(...items);
      });

      // Filter out items without client_id
      const validBillingItems = allBillingItems.filter((item) => item.client_id);

      if (validBillingItems.length > 0) {
        await billingItemService.createBillingItems(validBillingItems);
        triggerRefresh("billing item");
      }
      return validBillingItems.length;
    },
    [triggerRefresh, createBillingItemsForTimecard],
  );

  // Helper to create payroll items from patches (segment-based)
  const createPayrollItemsFromPatches = useCallback(
    async (patchIds: string[]) => {
      const timecards = await timecardService.getTimecards();
      const timecardMap = new Map(timecards.map((tc) => [tc.id, tc]));

      const allPayrollItems: any[] = [];
      patchIds.forEach((id) => {
        const timecard = timecardMap.get(id);
        if (!timecard) return;

        const items = createPayrollItemsForTimecard(timecard);
        allPayrollItems.push(...items);
      });

      // Filter out items without client_id
      const validPayrollItems = allPayrollItems.filter((item) => item.client_id);

      if (validPayrollItems.length > 0) {
        await payrollItemService.createPayrollItems(validPayrollItems);
        triggerRefresh("payroll item");
      }
      return validPayrollItems.length;
    },
    [triggerRefresh, createPayrollItemsForTimecard],
  );

  // Save confirmation for approving timecards
  const saveConfirmation: SaveConfirmationConfig<FromDB<TimecardType>> = useMemo(
    () => ({
      shouldConfirm: (patches) => patches.some((p) => p.status === "APPROVED"),
      storageKey: "timecard_approve_skip_confirmation",
      getDefaultExtraData: async (patches) => {
        const approvalPatches = patches.filter((p: any) => p.status === "APPROVED");
        const defaultCopyOption = (await AsyncStorage.getItem("timecard_approve_default_copy")) || "none";
        return {
          copyOption: defaultCopyOption as CopyOption,
          approvalPatchIds: approvalPatches.map((p: any) => p.id),
        };
      },
      renderModal: ({ patches, onConfirm, onCancel }) => {
        const approvalPatches = patches.filter((p) => p.status === "APPROVED");
        return (
          <ApproveTimecardModal
            count={approvalPatches.length}
            onConfirm={async (copyOption, dontShowAgain) => {
              // Save "don't show again" preference and default copy option
              if (dontShowAgain) {
                await AsyncStorage.setItem("timecard_approve_skip_confirmation", "true");
                await AsyncStorage.setItem("timecard_approve_default_copy", copyOption);
              }
              onConfirm({ copyOption, approvalPatchIds: approvalPatches.map((p) => p.id) });
            }}
            onCancel={onCancel}
          />
        );
      },
      onConfirmWithData: async (patches, extraData: { copyOption: CopyOption; approvalPatchIds: string[] }) => {
        // First, save the patches
        await timecardService.saveRowPatches(patches);

        // Then handle copy option
        const { copyOption, approvalPatchIds } = extraData;
        let billingCount = 0;
        let payrollCount = 0;

        if (copyOption === "billing" || copyOption === "both") {
          billingCount = await createBillingItemsFromPatches(approvalPatchIds);
        }
        if (copyOption === "payroll" || copyOption === "both") {
          payrollCount = await createPayrollItemsFromPatches(approvalPatchIds);
        }

        // Show additional toast if items were created
        if (billingCount > 0 || payrollCount > 0) {
          const parts: string[] = [];
          if (billingCount > 0) parts.push(`${billingCount} billing`);
          if (payrollCount > 0) parts.push(`${payrollCount} payroll`);
          toast.showToast({
            title: `Created ${parts.join(" and ")} item${billingCount + payrollCount > 1 ? "s" : ""}`,
            toastType: "success",
          });
        }
      },
    }),
    [toast, createBillingItemsFromPatches, createPayrollItemsFromPatches],
  );

  // Delete confirmation for deleting timecards
  const deleteConfirmation: DeleteConfirmationConfig<FromDB<TimecardType>> = useMemo(
    () => ({
      storageKey: "timecard_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteTimecardModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("timecard_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  return (
    <EntityTablePage<FromDB<TimecardType>>
      {...tableConfig}
      fetchData={timecardService.getTimecardsRPC}
      updateEntities={timecardService.saveRowPatches}
      deleteEntities={timecardService.deleteTimecardsByIds}
      tableConfig={{
        ...tableConfig.tableConfig,
        headerActions,
      }}
      rowNavIcon="dots-vertical"
      selectionActions={({ selectedIds, selectedMap }) => [
        {
          id: "copy-billing",
          label: "Copy to Billing",
          icon: "cash-register",
          onPress: () => headerActions[0].onPress({ selectedIds, selectedMap }),
        },
        {
          id: "copy-payroll",
          label: "Copy to Payroll",
          icon: "account-cash",
          onPress: () => headerActions[1].onPress({ selectedIds, selectedMap }),
        },
        {
          id: "copy-both",
          label: "Copy to Both",
          icon: "content-duplicate",
          onPress: () => headerActions[2].onPress({ selectedIds, selectedMap }),
        },
      ]}
      onAction={(row, ref) => {
        if (!ref) return;
        ref.current?.measureInWindow((x: number, y: number, _w: number, h: number) => {
          contextMenu.setLocation({ x, y: y + (h ?? 0) });
        });
        contextMenu.setMenuContent(menuContent(row));
        contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
        contextMenu.setVisible(true);
      }}
      saveConfirmation={saveConfirmation}
      deleteConfirmation={deleteConfirmation}
    />
  );
}

const styles = StyleSheet.create({
  menuItems: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
